import { useTranslation } from "react-i18next";
import { initI18n } from "./i18n";
import { useEffect, useMemo, useState } from "react";
import useDebounce from "./hooks/useDebounce";
import { ExternalLinkIcon, RefreshIcon } from "@heroicons/react/outline";
import { OnboardingTokenPayload } from "./interface";
import { jwtDecode } from "jwt-decode";

initI18n();

interface Props {
  token: string;
  onClickBack: () => void;
  onGenerateDocument: (id: string) => void;
}

function AppOnboardingData(props: Props) {
  const { t } = useTranslation();

  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);

  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 500);

  const [isLoading, setIsLoading] = useState(false);
  const [selectedId, setSelectedId] = useState<string>();

  const payload: OnboardingTokenPayload = useMemo(() => {
    if (props.token)
      return jwtDecode(props.token);

    return { portal_id: "" };
  }, [props.token])

  const portalId = payload.portal_id;

  useEffect(() => {
    setIsLoading(true);
    fetch(`${process.env.REACT_APP_API_ENDPOINT}/hubspot/onboarding/data/`, {
      method: "GET",
      headers: {
        "X-Token": props.token,
        'Content-Type': 'application/json',
      }
    })
      .then(res => res.json())
      .then(json => {
        const { total, results } = json;
        setTotal(total);
        setData(results);
      })
      .finally(() => setIsLoading(false));
  }, [debouncedSearch, props.token])

  return (
    <div className="flex flex-col gap-10 relative h-full ">

      <div className="flex flex-col items-center">
        <div className="font-gilroy font-semibold text-gray-600 text-3xl">{t("Select your test deal")}</div>
        <div className="font-semibold text-sm text-gray-400">{t("Choose a deal we can generate a document from")}</div>
      </div>

      <div className="flex flex-col items-center w-[800px] border rounded overflow-hidden">
        <div className="flex items-center border-b px-4 py-2 w-full justify-between bg-gray-50">
          <div className="flex items-center gap-2">
            <input type="text" className="w-96 px-2 py-1 border-2 rounded" placeholder="Search deals..." onChange={e => setSearch(e.currentTarget.value)} />
            {isLoading &&
              <RefreshIcon className="w-5 h-5 text-gray-600 animate-spin" />
            }
          </div>
          <div className="text-sm font-semibold text-gray-400">
            Total results: <span className="text-gray-600">{total}</span>
          </div>
        </div>

        <div className="flex flex-col w-full overflow-y-scroll" style={{ height: "calc(100vh - 500px)" }}>
          {(data).map(item => (
            <div key={item["id"]} className={`flex items-center justify-between w-full px-4 pb-1 pt-2 border-b last:border-0 cursor-pointer group ${selectedId === item["id"] ? "bg-blue-50" : "hover:bg-gray-100"}`} onClick={() => setSelectedId(id => id === item["id"] ? undefined : item["id"])}>
              <a className="flex items-center gap-1 font-gilroy font-semibold text-gray-600 group hover:underline" href={`https://app.hubspot.com/contacts/${portalId}/record/0-3/${item["id"]}/`} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}>
                {item["properties"]["dealname"]}
                <ExternalLinkIcon className="w-4 h-4 invisible group-hover:visible relative" />
              </a>
              {selectedId === item["id"]
                ? <div className="font-semibold text-blue">Selected</div>
                : <div className="invisible group-hover:visible font-semibold text-blue">Select</div>
              }
            </div>
          ))}
          {data.length === 0 &&
            <div className="m-auto text-gray-400 font-semibold">
              {isLoading
                ? "Loading..."
                : "No results"
              }
            </div>
          }
        </div>
      </div>

      <div className="absolute bottom-0 flex items-center justify-center gap-4 py-4 w-full border-t-2 flex-shrink-0">
        <button className="btn btn-gray" onClick={props.onClickBack}>Back</button>
        <button className="btn btn-blue" disabled={!selectedId} onClick={() => props.onGenerateDocument(selectedId!)}>Generate Document</button>
      </div>
    </div>
  );
}

export default AppOnboardingData;
